import React, { useState, useEffect } from 'react';
import CountryPhoneInput from './CountryPhoneInput';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const SettingsModal = ({ user, onClose, forceCompanyTab = false, initialTab }) => {
  const { t } = useLanguage();
  const isEditor = user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR');
  const isCustomer = user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER');
  const isCustomerOnly = isCustomer && !isEditor;
  const getDefaultTab = () => {
    if (isCustomerOnly) {
      return 'attendance';
    }

    if (forceCompanyTab) {
      return 'company';
    }

    return initialTab === 'affiliation' ? 'affiliation' : 'company';
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab);
  const [companyData, setCompanyData] = useState({
    companyName: '',
    phoneNumber: '',
    phoneCountryId: null,
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    gstinNumber: '',
    fssaiNumber: '',
    companyLevel: '',
    herbalifeLevel: '',
    transparencyMode: true,
    followupNotificationMinutes: 15,
    shareFollowupsWithSpouse: false,
    spousePhoneNumber: '',
    spousePhoneCountryId: null
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [hasExistingLogo, setHasExistingLogo] = useState(false);
  
  // Club affiliation state
  const [clubAffiliationType, setClubAffiliationType] = useState('CLUB_OWNER');
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const [systemEditors, setSystemEditors] = useState([]);
  const [affiliationLoading, setAffiliationLoading] = useState(false);
  const [affiliationError, setAffiliationError] = useState('');
  const [affiliationSuccess, setAffiliationSuccess] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  useEffect(() => {
    if (isCustomerOnly) {
      fetchTodayAttendanceStatus();
      setLoading(false);
      return;
    }

    fetchCompanyData();
    fetchUserAffiliation();
    fetchSystemEditors();
  }, [isCustomerOnly]);

  useEffect(() => {
    setActiveTab(getDefaultTab());
  }, [initialTab, isCustomerOnly, forceCompanyTab]);

  const fetchTodayAttendanceStatus = async () => {
    try {
      setAttendanceLoading(true);
      const response = await axios.get('/api/attendance/today/status');
      setTodayAttendance(response.data);
    } catch (error) {
      console.error("Failed to fetch today's attendance status:", error);
      setError(t("Failed to load today's attendance status. Please try again."));
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/company');
      const company = response.data;
      setCompanyData({
        companyName: company.companyName || '',
        phoneNumber: company.phoneNumber || '',
        phoneCountryId: company.phoneCountry?.id || null,
        email: company.email || '',
        addressLine1: company.addressLine1 || '',
        addressLine2: company.addressLine2 || '',
        city: company.city || '',
        state: company.state || '',
        pincode: company.pincode || '',
        gstinNumber: company.gstinNumber || '',
        fssaiNumber: company.fssaiNumber || '',
        companyLevel: company.companyLevel || '',
        herbalifeLevel: company.herbalifeLevel || '',
        transparencyMode: company.transparencyMode !== false,
        followupNotificationMinutes: company.followupNotificationMinutes ?? 15,
        shareFollowupsWithSpouse: company.shareFollowupsWithSpouse === true,
        spousePhoneNumber: company.spousePhoneNumber || '',
        spousePhoneCountryId: company.spousePhoneCountry?.id || null
      });
      setHasExistingLogo(company.hasLogo || false);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch company data:', error);
      }
      // If 404, it means no company data exists yet, which is fine
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('Please select an image file (PNG, JPG, GIF)'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('Logo file size must be less than 5MB'));
      return;
    }

    setLogoFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Check dimensions
        if (img.width > 240 || img.height > 240) {
          setError(t('Logo will be automatically resized to fit within 240x240 pixels'));
        }
        setLogoPreview(e.target.result);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate FSSAI number
    if (!companyData.fssaiNumber || companyData.fssaiNumber.trim().length < 14) {
      setError(t('Invalid FSSAI number'));
      setLoading(false);
      return;
    }

    const spousePhone = companyData.spousePhoneNumber.trim();
    if (spousePhone && !/^[0-9]{7,15}$/.test(spousePhone)) {
      setError(t('Spouse phone number must contain 7 to 15 digits'));
      setLoading(false);
      return;
    }
    if (companyData.shareFollowupsWithSpouse && !spousePhone) {
      setError(t('Spouse phone number is required when follow-up sharing is enabled'));
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      
      // Add company data as JSON
      formData.append('company', new Blob([JSON.stringify(companyData)], {
        type: 'application/json'
      }));
      
      // Add logo file if selected
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await axios.post('/api/company', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);
      setHasExistingLogo(response.data.hasLogo || false);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to save company:', error);
      setError(error.response?.data?.message || t('Failed to save company information. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm(t('Are you sure you want to delete the company logo?'))) {
      return;
    }

    try {
      await axios.delete('/api/company/logo');
      setHasExistingLogo(false);
      setLogoFile(null);
      setLogoPreview(null);
      setError('');
      alert(t('Logo deleted successfully'));
    } catch (error) {
      console.error('Failed to delete logo:', error);
      setError(t('Failed to delete logo. Please try again.'));
    }
  };

  const fetchUserAffiliation = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      const userData = response.data;
      
      if (userData.affiliation) {
        setClubAffiliationType('INTERN_CLUB_OWNER');
        setSelectedAffiliation(userData.affiliation.id);
      } else {
        setClubAffiliationType('CLUB_OWNER');
        setSelectedAffiliation(null);
      }
    } catch (error) {
      console.error('Failed to fetch user affiliation:', error);
    }
  };

  const fetchSystemEditors = async () => {
    try {
      const response = await axios.get('/api/admin/users-with-role/SYSTEM_EDITOR');
      // Filter out current user from the list
      const filteredEditors = response.data.filter(editor => editor.id !== user.id);
      setSystemEditors(filteredEditors);
    } catch (error) {
      console.error('Failed to fetch system editors:', error);
    }
  };

  const handleAffiliationSave = async () => {
    setAffiliationLoading(true);
    setAffiliationError('');

    try {
      const affiliationData = {
        affiliationType: clubAffiliationType,
        affiliationId: clubAffiliationType === 'INTERN_CLUB_OWNER' ? selectedAffiliation : null
      };

      await axios.put('/api/auth/club-affiliation', affiliationData);
      
      setAffiliationSuccess(true);
      setTimeout(() => {
        setAffiliationSuccess(false);
        // Refresh user affiliation data
        fetchUserAffiliation();
      }, 2000);
    } catch (error) {
      console.error('Failed to save club affiliation:', error);
      setAffiliationError(error.response?.data?.message || t('Failed to save club affiliation. Please try again.'));
    } finally {
      setAffiliationLoading(false);
    }
  };

  if (success || affiliationSuccess) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              {success ? t('Company Information Saved Successfully!') : t('Club Affiliation Updated Successfully!')}
            </h3>
            <p style={{ color: 'var(--gray-dark)' }}>
              {success ? t('Your company information has been updated.') : t('Your club affiliation has been updated.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content profile-modal">
        <div className="modal-header">
          <h3>{t('Settings')}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '15px' }}>⏳</div>
            <p style={{ color: 'var(--gray-dark)' }}>{t('Loading settings...')}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid var(--gray-medium)', 
          padding: '0 30px'
        }}>
          {isCustomerOnly ? (
            <button
              onClick={() => setActiveTab('attendance')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'attendance' ? 'var(--primary-blue)' : 'transparent',
                color: activeTab === 'attendance' ? 'var(--white)' : 'var(--gray-dark)',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontWeight: '500'
              }}
            >
              {t("Today's Attendance")}
            </button>
          ) : (
            <>
              <button
                onClick={() => setActiveTab('company')}
                disabled={forceCompanyTab}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: activeTab === 'company' ? 'var(--primary-blue)' : 'transparent',
                  color: activeTab === 'company' ? 'var(--white)' : 'var(--gray-dark)',
                  cursor: forceCompanyTab ? 'not-allowed' : 'pointer',
                  borderRadius: '6px 6px 0 0',
                  fontWeight: '500',
                  marginRight: '5px',
                  opacity: forceCompanyTab ? 0.7 : 1
                }}
              >
                {t('My Company')}
              </button>
              {!forceCompanyTab && (
                <button
                  onClick={() => setActiveTab('affiliation')}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    background: activeTab === 'affiliation' ? 'var(--primary-blue)' : 'transparent',
                    color: activeTab === 'affiliation' ? 'var(--white)' : 'var(--gray-dark)',
                    cursor: 'pointer',
                    borderRadius: '6px 6px 0 0',
                    fontWeight: '500'
                  }}
                >
                  {t('Club Affiliation')}
                </button>
              )}
            </>
          )}
        </div>

        {activeTab === 'attendance' && (
          <div style={{ padding: '30px' }}>
            {attendanceLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '24px', marginBottom: '15px' }}>⏳</div>
                <p style={{ color: 'var(--gray-dark)' }}>{t("Loading today's attendance...")}</p>
              </div>
            ) : todayAttendance?.marked ? (
              <div style={{
                background: 'linear-gradient(135deg, #E6FFFA 0%, #F0FFF4 100%)',
                border: '2px solid #48BB78',
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '56px', marginBottom: '12px' }}>✅</div>
                <h3 style={{ color: '#2F855A', marginBottom: '16px' }}>{t('Attendance Marked')}</h3>
                <div style={{ color: 'var(--dark-blue)', lineHeight: '1.8', fontSize: '16px' }}>
                  <div><strong>{t('Name')}:</strong> {todayAttendance.customerName}</div>
                  <div><strong>{t('Date')}:</strong> {todayAttendance.attendanceDateDisplay}</div>
                  <div><strong>{t('Marked At')}:</strong> {todayAttendance.markedAtDisplay || t('N/A')}</div>
                  <div><strong>{t('Club')}:</strong> {todayAttendance.companyName}</div>
                </div>
              </div>
            ) : (
              <div style={{
                background: '#FFF7ED',
                border: '2px solid #DD6B20',
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '56px', marginBottom: '12px' }}>📋</div>
                <h3 style={{ color: '#C05621', marginBottom: '16px' }}>{t('Yet To Mark Attendance')}</h3>
                <div style={{ color: 'var(--dark-blue)', lineHeight: '1.8', fontSize: '16px' }}>
                  <div><strong>{t('Name')}:</strong> {todayAttendance?.customerName || user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}</div>
                  <div><strong>{t('Date')}:</strong> {todayAttendance?.attendanceDateDisplay || new Date().toLocaleDateString()}</div>
                  <div><strong>{t('Club')}:</strong> {todayAttendance?.companyName || t('N/A')}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Company Tab */}
        {activeTab === 'company' && !loading && (
          <form onSubmit={handleSubmit} className="profile-form">
            {forceCompanyTab && (
              <div style={{
                background: '#FED7D7',
                border: '2px solid var(--error)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: '#C53030', marginBottom: '10px' }}>
                  {t('Company Setup Required')}
                </h4>
                <p style={{ color: '#C53030', margin: 0, fontSize: '14px' }}>
                  {t('Please complete your company information to continue using the platform. This information is required for invoice generation and compliance.')}
                </p>
              </div>
            )}
            
            <h4 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {t('Company Information')}
            </h4>

            <div className="form-group" style={{
              padding: '14px',
              border: '1px solid var(--gray-medium)',
              borderRadius: '8px',
              background: 'var(--gray-light)'
            }}>
              <label htmlFor="transparencyMode" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: 'var(--dark-blue)'
              }}>
                <input
                  type="checkbox"
                  id="transparencyMode"
                  checked={companyData.transparencyMode !== false}
                  onChange={(e) => setCompanyData({ ...companyData, transparencyMode: e.target.checked })}
                  disabled={loading}
                />
                {t('Transparency Mode')}
              </label>
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '6px' }}>
                {t('When enabled, customers can see program volume points and days attended.')}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="followupNotificationMinutes" className="form-label">
                {t('Notify me before (minutes) for follow-ups')}
              </label>
              <input
                type="number"
                id="followupNotificationMinutes"
                className="form-input"
                min="0"
                max="120"
                step="1"
                value={companyData.followupNotificationMinutes}
                onChange={(e) => setCompanyData({
                  ...companyData,
                  followupNotificationMinutes: Math.min(120, Math.max(0, parseInt(e.target.value || '0', 10)))
                })}
                required
                disabled={loading}
              />
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '6px' }}>
                {t('WhatsApp and mobile reminders will be sent between 0 and 120 minutes before each follow-up.')}
              </div>
            </div>

            <div className="form-group" style={{
              padding: '14px',
              border: '1px solid var(--gray-medium)',
              borderRadius: '8px',
              background: 'var(--gray-light)'
            }}>
              <label htmlFor="shareFollowupsWithSpouse" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: 'var(--dark-blue)'
              }}>
                <input
                  type="checkbox"
                  id="shareFollowupsWithSpouse"
                  checked={companyData.shareFollowupsWithSpouse}
                  onChange={(e) => setCompanyData({ ...companyData, shareFollowupsWithSpouse: e.target.checked })}
                  disabled={loading}
                />
                {t('Allow sharing follow-up reminders with spouse')}
              </label>
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '6px' }}>
                {t('When enabled, lead and customer follow-up WhatsApp reminders are also sent to your spouse. Mobile push notifications are not shared.')}
              </div>

              {companyData.shareFollowupsWithSpouse && (
                <div style={{ marginTop: '14px' }}>
                  <label htmlFor="spousePhoneNumber" className="form-label">
                    {t('Spouse Phone Number')} *
                  </label>
                  <CountryPhoneInput
                    phoneValue={companyData.spousePhoneNumber}
                    countryValue={companyData.spousePhoneCountryId}
                    onPhoneChange={(phone) => setCompanyData({ ...companyData, spousePhoneNumber: phone })}
                    onCountryChange={(countryId) => setCompanyData({ ...companyData, spousePhoneCountryId: countryId })}
                    disabled={loading}
                    required={true}
                    placeholder="Enter spouse phone number"
                  />
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '6px' }}>
                    {t('Enter 7 to 15 digits without spaces. Turning sharing off will preserve this number.')}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="companyName" className="form-label">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                className="form-input"
                value={companyData.companyName}
                onChange={(e) => setCompanyData({...companyData, companyName: e.target.value})}
                required
                disabled={loading}
                placeholder="e.g., ABC Wellness Pvt Ltd"
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="phoneNumber" className="form-label">
                  Phone Number
                </label>
                <CountryPhoneInput
                  phoneValue={companyData.phoneNumber}
                  countryValue={companyData.phoneCountryId}
                  onPhoneChange={(phone) => setCompanyData({...companyData, phoneNumber: phone})}
                  onCountryChange={(countryId) => setCompanyData({...companyData, phoneCountryId: countryId})}
                  disabled={loading}
                  required={false}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  value={companyData.email}
                  onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                  disabled={loading}
                  placeholder="info@company.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="addressLine1" className="form-label">
                Address Line 1
              </label>
              <input
                type="text"
                id="addressLine1"
                className="form-input"
                value={companyData.addressLine1}
                onChange={(e) => setCompanyData({...companyData, addressLine1: e.target.value})}
                disabled={loading}
                placeholder="123 Business Park"
              />
            </div>

            <div className="form-group">
              <label htmlFor="addressLine2" className="form-label">
                Address Line 2
              </label>
              <input
                type="text"
                id="addressLine2"
                className="form-input"
                value={companyData.addressLine2}
                onChange={(e) => setCompanyData({...companyData, addressLine2: e.target.value})}
                disabled={loading}
                placeholder="Near Metro Station"
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="city" className="form-label">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  className="form-input"
                  value={companyData.city}
                  onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
                  disabled={loading}
                  placeholder="Mumbai"
                />
              </div>

              <div className="form-group">
                <label htmlFor="state" className="form-label">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  className="form-input"
                  value={companyData.state}
                  onChange={(e) => setCompanyData({...companyData, state: e.target.value})}
                  disabled={loading}
                  placeholder="Maharashtra"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pincode" className="form-label">
                Pincode
              </label>
              <input
                type="text"
                id="pincode"
                className="form-input"
                value={companyData.pincode}
                onChange={(e) => setCompanyData({...companyData, pincode: e.target.value})}
                disabled={loading}
                placeholder="400001"
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="gstinNumber" className="form-label">
                  GSTIN Number (Optional)
                </label>
                <input
                  type="text"
                  id="gstinNumber"
                  className="form-input"
                  value={companyData.gstinNumber}
                  onChange={(e) => setCompanyData({...companyData, gstinNumber: e.target.value})}
                  disabled={loading}
                  placeholder="27ABCDE1234F1Z5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="fssaiNumber" className="form-label">
                  FSSAI Number *
                </label>
                <input
                  type="text"
                  id="fssaiNumber"
                  className="form-input"
                  value={companyData.fssaiNumber}
                  onChange={(e) => setCompanyData({...companyData, fssaiNumber: e.target.value})}
                  disabled={loading}
                  placeholder="12345678901234"
                  required
                  minLength={14}
                />
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                  FSSAI number must be at least 14 characters
                </div>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="companyLevel" className="form-label">
                  Company Discount Level
                </label>
                <select
                  id="companyLevel"
                  className="form-input"
                  value={companyData.companyLevel || ''}
                  onChange={(e) => setCompanyData({...companyData, companyLevel: e.target.value})}
                  disabled={loading}
                >
                  <option value="">Select Company Discount Level</option>
                  <option value="BRONZE">Bronze</option>
                  <option value="SILVER">Silver</option>
                  <option value="GOLD">Gold</option>
                  <option value="SUCCESS_BUILDER">Success Builder</option>
                  <option value="SUPERVISOR">Supervisor</option>
                </select>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                  Company level used for profit calculations based on product pricing tiers
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="herbalifeLevel" className="form-label">
                  Herbalife Level
                </label>
                <select
                  id="herbalifeLevel"
                  className="form-input"
                  value={companyData.herbalifeLevel || ''}
                  onChange={(e) => setCompanyData({...companyData, herbalifeLevel: e.target.value})}
                  disabled={loading}
                >
                  <option value="">Select Herbalife Level</option>
                  <option value="Bronze">Bronze</option>
                  <option value="Silver">Silver</option>
                  <option value="Gold">Gold</option>
                  <option value="Success Builder">Success Builder</option>
                  <option value="Supervisor">Supervisor</option>
                  <option value="World Team">World Team</option>
                  <option value="Active World Team">Active World Team</option>
                  <option value="GET Team">GET Team</option>
                  <option value="GET Team 2500 RO">GET Team 2500 RO</option>
                  <option value="Millionaire Team">Millionaire Team</option>
                  <option value="Millionaire 7500 RO">Millionaire 7500 RO</option>
                  <option value="President Team">President Team</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="logo" className="form-label">
                Company Logo
              </label>
              <input
                type="file"
                id="logo"
                className="form-input"
                onChange={handleLogoChange}
                disabled={loading}
                accept="image/png,image/jpeg,image/jpg,image/gif"
              />
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                Supported formats: PNG, JPG, GIF. Maximum size: 5MB. Will be resized to fit within 240x240 pixels.
              </div>
              
              {/* Logo Preview */}
              {logoPreview && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                    Logo Preview:
                  </div>
                  <img 
                    src={logoPreview} 
                    alt="Logo preview" 
                    style={{ 
                      maxWidth: '120px', 
                      maxHeight: '120px', 
                      border: '1px solid var(--gray-medium)',
                      borderRadius: '4px'
                    }} 
                  />
                </div>
              )}
              
              {/* Existing Logo */}
              {hasExistingLogo && !logoPreview && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '5px' }}>
                    Current Logo:
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      padding: '10px', 
                      border: '1px solid var(--gray-medium)', 
                      borderRadius: '4px',
                      background: 'var(--gray-light)'
                    }}>
                      📷 Logo uploaded
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                      onClick={handleDeleteLogo}
                    >
                      Delete Logo
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              {!forceCompanyTab && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t('Cancel')}
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: forceCompanyTab ? '100%' : 'auto' }}
              >
                {loading ? t('Saving...') : (forceCompanyTab ? t('Complete Setup & Continue') : t('Save Company Information'))}
              </button>
            </div>
          </form>
        )}

        {/* Club Affiliation Tab */}
        {activeTab === 'affiliation' && !loading && (
          <div style={{ padding: '30px' }}>
            <h4 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {t('Club Affiliation Settings')}
            </h4>

            {affiliationError && (
              <div className="alert alert-error">{affiliationError}</div>
            )}

            <div className="form-group">
              <label className="form-label">
                {t('Club Ownership Type')} *
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="affiliationType"
                    value="CLUB_OWNER"
                    checked={clubAffiliationType === 'CLUB_OWNER'}
                    onChange={(e) => {
                      setClubAffiliationType(e.target.value);
                      setSelectedAffiliation(null);
                    }}
                    disabled={affiliationLoading}
                  />
                  <span>{t('Club Owner')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="affiliationType"
                    value="INTERN_CLUB_OWNER"
                    checked={clubAffiliationType === 'INTERN_CLUB_OWNER'}
                    onChange={(e) => setClubAffiliationType(e.target.value)}
                    disabled={affiliationLoading}
                  />
                  <span>{t('Intern Club Owner')}</span>
                </label>
              </div>
            </div>

            {clubAffiliationType === 'INTERN_CLUB_OWNER' && (
              <div className="form-group">
                <label htmlFor="affiliation" className="form-label">
                  {t('Select Club Owner')} *
                </label>
                <select
                  id="affiliation"
                  className="form-input"
                  value={selectedAffiliation || ''}
                  onChange={(e) => setSelectedAffiliation(e.target.value ? parseInt(e.target.value) : null)}
                  required={clubAffiliationType === 'INTERN_CLUB_OWNER'}
                  disabled={affiliationLoading}
                >
                  <option value="">Select a club owner</option>
                  {systemEditors.map(editor => (
                    <option key={editor.id} value={editor.id}>
                      {editor.fullName} ({editor.email})
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                  {t('Select the club owner you are affiliated with')}
                </div>
              </div>
            )}

            {/* Current Affiliation Display */}
            {user.affiliation && (
              <div style={{
                background: 'var(--pale-blue)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '2px solid var(--primary-blue)'
              }}>
                <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  {t('Current Club Affiliation')}
                </h5>
                <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                  <div><strong>{t('Type')}:</strong> {t('Intern Club Owner')}</div>
                  <div><strong>{t('Affiliated with')}:</strong> {user.affiliation.fullName} ({user.affiliation.email})</div>
                </div>
              </div>
            )}

            {!user.affiliation && clubAffiliationType === 'CLUB_OWNER' && (
              <div style={{
                background: 'var(--pale-blue)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '2px solid var(--primary-blue)'
              }}>
                <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  {t('Current Club Affiliation')}
                </h5>
                <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                  <div><strong>{t('Type')}:</strong> {t('Club Owner')}</div>
                  <div>{t('You are operating as an independent club owner')}</div>
                </div>
              </div>
            )}

            <div style={{
              background: '#FFF3CD',
              border: '1px solid #FFEAA7',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>{t('About Club Affiliation')}:</strong>
              <ul style={{ marginTop: '8px', marginLeft: '20px', fontSize: '13px' }}>
                <li><strong>{t('Club Owner')}:</strong> {t('You operate independently and manage your own club')}</li>
                <li><strong>{t('Intern Club Owner')}:</strong> {t('You are affiliated with another club owner and operate under their guidance')}</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={affiliationLoading}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAffiliationSave}
                disabled={affiliationLoading || (clubAffiliationType === 'INTERN_CLUB_OWNER' && !selectedAffiliation)}
              >
                {affiliationLoading ? t('Saving...') : t('Save Affiliation')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsModal;
